import { List } from 'immutable';
import { v4 as uuid } from 'uuid';

import { extractPortfolioDataForAI } from 'app-components/flux/ai-insights/transformers/investmentsTransformers';
import { investmentHoldingsV2Selectors } from 'app-components/flux/investment-holdings-v2';
import { investmentQuotesV2Selectors } from 'app-components/flux/investment-quotes-v2';
import { getLogger } from 'app-components/utils/core';
import tracker from 'app-components/utils/core/tracker';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { AI_CHAT_REDUCER_KEY } from '../../aiChatTypes';
import { safeParse } from '../../aiChatUtils';
import { cacheQueryResult } from '../../aiQueryCacheSlice';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';

const log = getLogger('llmInvestmentsTools');

const DEFAULT_MAX_POSITIONS = 50;
const MAX_POSITIONS_CAP = 200;

const collectUniqueStrings = (values?: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue || seen.has(trimmedValue)) {
      continue;
    }

    seen.add(trimmedValue);
    result.push(trimmedValue);
  }

  return result;
};

const sanitizeAccountIds = (accountIds?: unknown): List<string> | undefined => {
  const cleaned = collectUniqueStrings(accountIds);
  return cleaned.length ? List(cleaned) : undefined;
};

const sanitizeMaxPositions = (value?: unknown): number => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return DEFAULT_MAX_POSITIONS;
  }

  return Math.min(Math.floor(numericValue), MAX_POSITIONS_CAP);
};

const sanitizeSymbols = (symbols?: unknown): string[] => collectUniqueStrings(symbols);

const sanitizeFields = (fields?: unknown): string[] | undefined => {
  const cleaned = collectUniqueStrings(fields);
  return cleaned.length ? cleaned : undefined;
};

export const investmentsTools = {
  get_investment_holdings: {
    definition: {
      name: 'get_investment_holdings',
      description:
        "Returns the user's investment portfolio summary along with individual holdings (positions) sourced from their connected accounts. " +
        'Use this when you need the latest holdings, their value, and quote-driven insights. ' +
        'Optionally filter by account IDs and control how many positions to include. ' +
        'When this tool returns a resultId, pass it to render_chart to show a chart of holdings by market value. ' +
        'Do not offer to create watchlists, recategorize investment transactions, or generate investment reports. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {
          accountIds: {
            type: 'array' as const,
            items: { type: 'string' as const },
            description:
              'Optional list of account IDs to scope the portfolio (e.g., only brokerage accounts the user referenced). Defaults to all investment accounts.',
          },
          includePositions: {
            type: 'boolean' as const,
            description:
              'When true (default), returns detailed holdings. Set to false if you only need aggregate portfolio metrics.',
          },
          maxPositions: {
            type: 'number' as const,
            description:
              'Maximum number of positions to include when includePositions is true. Defaults to 50 and capped at 200 to prevent very large responses.',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      tracker.track(tracker.events.AIChatInvestInfo);

      const args = safeParse(msg.arguments) || {};
      const { accountIds, includePositions = true, maxPositions } = args;

      const state = store?.getState() as RootState | undefined;

      if (!state) {
        log.error('llmTools/investments: Redux store unavailable.');
        return returnToolResponse(msg, {
          portfolioSummary: null,
          positions: [],
          message: 'Investments are unavailable right now.',
        });
      }

      const accountFilter = sanitizeAccountIds(accountIds);
      const holdings = investmentHoldingsV2Selectors.getInvestmentHoldingNodes(state, accountFilter);

      if (!holdings || holdings.size === 0) {
        log.debug('llmTools/investments: No holdings found for provided filters.');
        return returnToolResponse(msg, {
          portfolioSummary: null,
          positions: [],
          message: 'No investment holdings found.',
        });
      }

      const includeDetailedPositions = Boolean(includePositions);
      const portfolio = extractPortfolioDataForAI({
        holdings,
        includeHoldings: true,
      });

      if (!portfolio) {
        log.warn('llmTools/investments: Failed to build portfolio response.');
        return returnToolResponse(msg, {
          portfolioSummary: null,
          positions: [],
          message: 'Unable to build portfolio data.',
        });
      }

      const limit = sanitizeMaxPositions(maxPositions);
      const positions =
        includeDetailedPositions && portfolio.positions
          ? portfolio.positions.slice(0, Math.min(limit, portfolio.positions.length))
          : [];

      const conversationId =
        (store?.getState() as RootState | undefined)?.[AI_CHAT_REDUCER_KEY]?.currentConversationId ?? 'session';
      const chartRows = positions
        .filter((p) => p.marketValue > 0)
        .map((p) => ({ category: p.symbol || p.name, amount: p.marketValue }));

      let resultId: string | undefined;
      if (chartRows.length >= 2) {
        resultId = `qr_${uuid()}`;
        store?.dispatch(
          cacheQueryResult({
            resultId,
            conversationId,
            queryKind: 'investment_holdings',
            fullData: chartRows,
            summary: {
              text: `Investment holdings breakdown (${chartRows.length} positions)`,
              keyFacts: { positionCount: chartRows.length, totalValue: portfolio.portfolioSummary.totalValue },
            },
            dataShape: 'breakdown',
            cachedAt: Date.now(),
          }),
        );
      }

      return returnToolResponse(msg, {
        portfolioSummary: portfolio.portfolioSummary,
        positions,
        meta: {
          totalPositions: portfolio.portfolioSummary.totalPositions,
          returnedPositions: positions.length,
          filters: accountFilter ? { accountIds: accountFilter.toArray() } : undefined,
        },
        ...(resultId && { resultId, availableRenderHints: ['pie', 'bar', 'sidewaysBar'] }),
      });
    },
  },

  get_investment_quotes: {
    definition: {
      name: 'get_investment_quotes',
      description:
        'Looks up the latest cached market data for one or more securities. Provide the ticker symbols you need and the tool returns price, change, and other quote fields when available. ' +
        'Do not offer to create watchlists, recategorize investment transactions, or generate investment reports. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {
          symbols: {
            type: 'array' as const,
            items: { type: 'string' as const },
            description:
              'List of ticker symbols to look up (e.g., ["AAPL","MSFT"]). At least one symbol is required and duplicates are ignored.',
          },
          fields: {
            type: 'array' as const,
            items: { type: 'string' as const },
            description:
              'Optional subset of quote fields to return (e.g., ["currentPrice","pctChange","volume"]). When omitted, all available fields are returned.',
          },
        },
        required: ['symbols'],
      },
    },
    handler: (msg: ToolMessage) => {
      tracker.track(tracker.events.AIChatInvestInfo);

      const args = safeParse(msg.arguments) || {};
      const { symbols, fields } = args;

      const sanitizedSymbols = sanitizeSymbols(symbols);

      if (!sanitizedSymbols.length) {
        log.warn('llmTools/investments: get_investment_quotes called without symbols.');
        return returnToolResponse(msg, {
          quotes: [],
          missingSymbols: [],
          message: 'You must provide at least one symbol.',
        });
      }

      const state = store?.getState() as RootState | undefined;

      if (!state) {
        log.error('llmTools/investments: Redux store unavailable for quotes.');
        return returnToolResponse(msg, {
          quotes: [],
          missingSymbols: sanitizedSymbols,
          message: 'Quotes are unavailable right now.',
        });
      }

      const allowedFields = sanitizeFields(fields);
      const foundQuotes: Array<{ symbol: string; quote: Record<string, unknown> }> = [];
      const missingSymbols: string[] = [];

      sanitizedSymbols.forEach((symbol) => {
        const quoteRecord = investmentQuotesV2Selectors.getInvestmentQuoteBySymbol(state, symbol);
        const quoteData = quoteRecord?.toJS?.() ?? quoteRecord;

        if (!quoteData) {
          missingSymbols.push(symbol);
          return;
        }

        const plainQuote = quoteData as Record<string, unknown>;

        if (allowedFields && allowedFields.length) {
          const filtered = allowedFields.reduce<Record<string, unknown>>((acc, fieldName) => {
            if (fieldName in plainQuote) {
              acc[fieldName] = plainQuote[fieldName];
            }
            return acc;
          }, {});
          foundQuotes.push({ symbol, quote: { symbol, ...filtered } });
          return;
        }

        foundQuotes.push({ symbol, quote: plainQuote });
      });

      return returnToolResponse(msg, {
        quotes: foundQuotes,
        missingSymbols,
      });
    },
  },
};
